/**
 * Firestore-backed notes storage.
 * Path matches plugin/lectura notes-core:
 * users/{uid}/presentations/{presId}/slideNotes/{slideIndex}
 */

import type { NotesStorage } from '../core/types';
import { ensureFirestoreDb, getUserSlideNoteDoc, type FirestoreServiceOptions } from './firestore-service';

interface CreateFirestoreNotesStorageOptions extends FirestoreServiceOptions {}

function key(presId: string, slideIndex: number, uid: string): string {
  return `${presId}|${slideIndex}|${uid}`;
}

function toMillis(raw: unknown): number {
  if (typeof raw === 'number') return raw;
  if (raw && typeof raw === 'object' && 'toMillis' in (raw as { toMillis?: unknown }) && typeof (raw as { toMillis?: () => number }).toMillis === 'function') {
    return (raw as { toMillis: () => number }).toMillis();
  }
  return 0;
}

export function createFirestoreNotesStorage(opts: CreateFirestoreNotesStorageOptions): NotesStorage {
  const cache = new Map<string, { text: string; updatedAt: number }>();

  return {
    getNote(presId: string, slideIndex: number, uid: string): { text: string } {
      const cached = cache.get(key(presId, slideIndex, uid));
      return { text: cached?.text || '' };
    },

    async getNoteAsync(presId: string, slideIndex: number, uid: string): Promise<{ text: string }> {
      const k = key(presId, slideIndex, uid);
      const db = await ensureFirestoreDb(opts);
      const ref = getUserSlideNoteDoc(db, uid, presId, slideIndex) as unknown as { get(): Promise<{ exists: boolean; data(): Record<string, unknown> }> };
      const snap = await ref.get();
      if (!snap.exists) {
        cache.set(k, { text: '', updatedAt: 0 });
        return { text: '' };
      }
      const d = snap.data() || {};
      const noteText = String(d.text ?? '');
      cache.set(k, { text: noteText, updatedAt: toMillis(d.updatedAt) });
      return { text: noteText };
    },

    setNote(presId: string, slideIndex: number, uid: string, data: { text: string }): Promise<void> {
      const k = key(presId, slideIndex, uid);
      const nextText = String(data.text || '');
      cache.set(k, { text: nextText, updatedAt: Date.now() });
      return ensureFirestoreDb(opts).then((db) => {
        const ref = getUserSlideNoteDoc(db, uid, presId, slideIndex) as unknown as {
          set(data: Record<string, unknown>, opts?: { merge?: boolean }): Promise<void>;
        };
        return ref.set(
          {
            text: nextText,
            updatedAt: Date.now()
          },
          { merge: true }
        );
      });
    }
  };
}
